// ── SalesAccountsPage — all sales-side accounts (every lifecycle stage) ──
import React, { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { localFilter, exportCSV, fmt } from "../../../shared/format";
import { Badge, Btn, Card, ErrorMsg, ExportBtn, Icon, PageLink, STh, SearchBar, SectionHeader, Spinner, Td, TdNum, Th } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

// A lost stage, decided from the lookup row. Prefers a real flag if dbo
// .LifecycleStages ever grows one; otherwise reads the name, which covers
// "Lost", "Closed Lost", "Declined (Lost)" and friends without a hard-coded
// list that goes stale the moment someone renames a stage.
const isLostStage = (s) => {
  if (s && typeof s === "object") {
    if (s.is_lost != null) return !!s.is_lost;
    if (s.is_dead != null) return !!s.is_dead;
  }
  const n = String((s && s.stage) || s || "").toLowerCase();
  return n.includes("lost") || n.includes("declined") || n.includes("dead");
};

export const SalesAccountsPage = () => {
  const { api, role, go, rowNav, setModal, setConfirm, delRec } = useApp();
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true); const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [stages, setStages] = useState([]);
  // Lost accounts are history. They stay one click away, but they shouldn't be
  // padding out the list — or the subtotals — of the book you actually work.
  const [hideLost, setHideLost] = useState(true);

  const load = () => { setLoading(true); api.get("getSalesAccounts").then(d => { setRows(Array.isArray(d) ? d : []); setLoading(false); }).catch(e => { setErr(e.message); setLoading(false); }); };
  // A record deleted or changed in a slim (popup) window posts this back to
  // the window that opened it, so the list here drops the row without a reload.
  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data && e.data.type === "ace:records-changed" && e.data.table === "Accounts") load();
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
    api.get("getLifecycleStages").then(d => setStages(Array.isArray(d) ? d : [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stage options: prefer the ordered lookup; fall back to whatever's in the data.
  const stageOpts = stages.length
    ? stages.map(s => s.stage)
    : [...new Set(rows.map(a => a.lifecycle_stage).filter(Boolean))];

  // Which stages count as "lost". An explicit flag on dbo.LifecycleStages wins
  // if you ever add one; otherwise it falls back to reading the name, so this
  // works whether the stage is called "Lost", "Closed Lost" or "Declined".
  const lostStageNames = new Set(
    (stages.length ? stages : stageOpts.map(st => ({ stage: st })))
      .filter(isLostStage).map(st => st.stage)
  );

  const stageFiltered = stageFilter ? rows.filter(a => (a.lifecycle_stage || "") === stageFilter) : rows;
  // Picking a lost stage explicitly overrides the hide — otherwise choosing
  // "Lost" from the dropdown would show an empty table, which reads as a bug.
  const lostHidden = hideLost && !(stageFilter && lostStageNames.has(stageFilter));
  const visibleRows = lostHidden
    ? stageFiltered.filter(a => !lostStageNames.has(a.lifecycle_stage || ""))
    : stageFiltered;
  const lostCount = stageFiltered.length - visibleRows.length;
  const filtered = localFilter(visibleRows, q, ["companyname", "accountnumber", "lifecycle_stage", "sales_owner_name", "city", "state", "companyemail", "companyphone", "website"]);
  const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, 'companyname', 'asc');
  const reset = () => {};   // kept so the existing filter handlers stay unchanged

  // ── Sections by lifecycle stage ────────────────────────────────────────────
  // Order comes from dbo.LifecycleStages.sort_order, so the sections follow the
  // pipeline rather than the alphabet, and adding a stage in SQL reorders the
  // page with no code change. Any stage present in the data but missing from
  // the lookup still gets a section at the end — a lead should never vanish
  // because a lookup row was deleted.
  const orderedStages = stages.length
    ? [...stages].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999)).map(s => s.stage)
    : [];
  const seenStages = [...new Set(sorted.map(a => a.lifecycle_stage || "No Stage"))];
  const groupOrder = [
    ...orderedStages,
    ...seenStages.filter(st => !orderedStages.includes(st)).sort(),
  ];

  const groups = groupOrder
    .map(stage => ({ stage, rows: sorted.filter(a => (a.lifecycle_stage || "No Stage") === stage) }))
    .filter(g => g.rows.length > 0);

  const sub = arr => ({
    contacts: arr.reduce((s, a) => s + (a.contact_count || 0), 0),
    opps:     arr.reduce((s, a) => s + (a.opp_count || 0), 0),
    openAmt:  arr.reduce((s, a) => s + (parseFloat(a.open_opp_amount) || 0), 0),
  });

  if (loading) return <Spinner />; if (err) return <ErrorMsg msg={err} onRetry={load} />;
  return <>
    <SectionHeader actions={[
      <SearchBar key="s" value={q} onChange={v => { setQ(v); reset(); }} placeholder="Name, account #, email, phone..." />,
      <select key="stage" value={stageFilter} onChange={e => { setStageFilter(e.target.value); reset(); }}
        style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid var(--color-border-secondary)", fontSize: 13, background: "var(--color-background-primary)", color: "var(--color-text-primary)", cursor: "pointer" }}>
        <option value="">All Stages</option>
        {stageOpts.map(s => <option key={s} value={s}>{s}</option>)}
      </select>,
      <label key="lost" title={lostStageNames.size ? "Hides: " + [...lostStageNames].join(", ") : "No lost stages found"}
        style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
        <input type="checkbox" checked={hideLost} onChange={e => { setHideLost(e.target.checked); reset(); }} />
        Hide lost{lostHidden && lostCount ? ` (${lostCount})` : ""}
      </label>,
      role.canEdit ? <Btn key="add" variant="primary" onClick={() => setModal({ type: "account-add", onDone: load })}>+ Add Account</Btn> : null,
      <ExportBtn key="ex" onClick={() => exportCSV(sorted, ["companyname", "accountnumber", "lifecycle_stage", "sales_owner_name", "contact_count", "opp_count", "open_opp_amount"], "sales_accounts.csv")}>Export CSV</ExportBtn>
    ]} />
    <Card style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
        <thead><tr>
          <STh label="Company"     sortKey="companyname"      currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Account #"   sortKey="accountnumber"    currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Stage"       sortKey="stage_order"      currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Sales Owner" sortKey="sales_owner_name" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="PM Visible"  sortKey="portfolio_visible" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Contacts"    sortKey="contact_count"    currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <STh label="Opps"        sortKey="opp_count"        currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <STh label="Open Opp $"  sortKey="open_opp_amount"  currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <Th></Th>
        </tr></thead>
        <tbody>
          {groups.map(group => {
            const t = sub(group.rows);
            return (
              <React.Fragment key={"g-" + group.stage}>
                <tr>
                  <td colSpan={9} style={{ padding: "8px 12px", background: B.cyanLight }}>
                    <Badge s={group.stage} />
                    <span style={{ marginLeft: 8, color: B.cyanDark, fontSize: 11, fontWeight: 600 }}>
                      {group.rows.length} account{group.rows.length !== 1 ? "s" : ""}
                    </span>
                  </td>
                </tr>

                {group.rows.map(a => (
                  <tr key={a.id} style={{ cursor: "pointer" }} {...rowNav("sales-account", { accountid: a.id, companyname: a.companyname })}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <Td style={{ fontWeight: 500 }}><PageLink page="sales-account" params={{ accountid: a.id, companyname: a.companyname }}>{a.companyname}</PageLink></Td>
                    <Td><span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{a.accountnumber || "—"}</span></Td>
                    <Td><Badge s={a.lifecycle_stage} /></Td>
                    <Td>{a.sales_owner_name || "—"}</Td>
                    <Td>{a.portfolio_visible ? <span style={{ fontSize: 11, color: "#1a7f4b", fontWeight: 600 }}>Yes</span> : <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>No</span>}</Td>
                    <TdNum>{a.contact_count || 0}</TdNum>
                    <TdNum>{a.opp_count || 0}</TdNum>
                    <TdNum>{fmt(a.open_opp_amount || 0)}</TdNum>
                    {/* stopPropagation, or the buttons also fire the row's
                        navigation and the dialog is lost on the way out. */}
                    <Td><div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }} onClick={e => e.stopPropagation()}>
                      <Btn onClick={() => go("sales-account", { accountid: a.id, companyname: a.companyname })}><Icon type="view" /></Btn>
                      {/* canDelete is the Admin role and only the Admin role
                          (ROLE_PERMS in shared/auth.js), so this is the
                          admins-only control it reads as.
                          It is not a quiet delete: deleteRecord refuses while
                          contacts, opportunities, loans or notes hang off the
                          account and answers with the counts, so what a
                          mis-click produces is a sentence, not a hole. */}
                      {role.canDelete && <Btn variant="danger" title={"Delete " + (a.companyname || "this account")}
                        onClick={() => setConfirm({
                          msg: 'Delete "' + (a.companyname || "this account") + '"?',
                          onConfirm: () => delRec("Accounts", a.id, "Deleted account " + (a.companyname || a.id), load) })}>
                        <Icon type="delete" /></Btn>}
                    </div></Td>
                  </tr>
                ))}

                <tr style={{ background: "var(--color-background-secondary)", borderTop: "1.5px solid " + B.cyanLight }}>
                  <td colSpan={5} style={{ padding: "8px 12px", fontSize: 12, fontWeight: 600, color: B.cyanDark, textAlign: "right" }}>
                    {group.stage} Subtotal ({group.rows.length})
                  </td>
                  <TdNum style={{ fontWeight: 600, color: B.cyanDark }}>{t.contacts}</TdNum>
                  <TdNum style={{ fontWeight: 600, color: B.cyanDark }}>{t.opps}</TdNum>
                  <TdNum style={{ fontWeight: 600, color: B.cyanDark }}>{fmt(t.openAmt)}</TdNum>
                  <td />
                </tr>
              </React.Fragment>
            );
          })}

          {groups.length === 0 && (
            <tr><Td colSpan={9} style={{ color: "var(--color-text-secondary)" }}>No accounts match.</Td></tr>
          )}
        </tbody>
        <tfoot><tr style={{ background: "var(--color-background-secondary)" }}>
          <td colSpan={5} style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)" }}>Total ({sorted.length})</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{sorted.reduce((s, a) => s + (a.contact_count || 0), 0)}</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{sorted.reduce((s, a) => s + (a.opp_count || 0), 0)}</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{fmt(sorted.reduce((s, a) => s + (parseFloat(a.open_opp_amount) || 0), 0))}</td>
          <td style={{ borderTop: "1.5px solid var(--color-border-secondary)" }} />
        </tr></tfoot>
      </table>
    </Card>
  </>;
};
